import { zodResolver } from "@hookform/resolvers/zod";
import {
  InternalMessagesForm,
  internalMessagesFormSchema,
} from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import {
  internalMessagesFormDefault,
  internalMessagesResponseToForm,
} from "features/CenterContent/RoleContent/InternalMessage/schema/transformers";
import {
  createInternalMessage,
  ResponseWithVisibility,
} from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import { useOperationalTextContexts } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { VisibilityForm } from "shared/form/visibilitySchema";

export const useFormInternalMessage = (
  internalMessages: InternalMessageResponse[] | undefined,
  // Undefined for internal messages related to events, as they copies the event's visibility
  visibility?: VisibilityForm,
) => {
  const internalMessagesWithoutEvaluationForm = internalMessages?.filter(
    (message) => !message.evaluationForm,
  );
  const formMethods = useFormVerbose<InternalMessagesForm>({
    resolver: zodResolver(internalMessagesFormSchema),
    defaultValues: {
      internalMessages: internalMessagesWithoutEvaluationForm
        ? internalMessagesResponseToForm(internalMessagesWithoutEvaluationForm)
        : internalMessagesFormDefault(),
      visibility,
    },
  });

  const contexts = useOperationalTextContexts("INTERNAL");
  useEffect(() => {
    if (!contexts.data) return;
    const contextNames = contexts.data
      .map((it) => it.type)
      .filter((it) => it !== "DROPS_LOG")
      .filter((it) => it !== "POCKET_TROLL");
    // Only set the context for the internal message you are creating/editing
    formMethods.setValue("internalMessages.0.contexts", contextNames);
  }, [contexts.data]);

  return formMethods;
};

export const handleInternalMessageSubmit =
  (formMethods: UseFormReturn<InternalMessagesForm>) =>
  /**
   * Train/Station/Infrastructure/Global all have a response with uuid and visibility
   * The internalMessage is generated with this information
   */
  (response: ResponseWithVisibility, relatedEventPrevUuid?: string) => {
    /**
     * If the internal message is empty we skip the handleSubmit,
     * thus prevent console.log() and trigger() -> errors from occuring
     */
    const internalMessages = formMethods.getValues("internalMessages");
    const latestInternalMessage = internalMessages[0];
    if (latestInternalMessage.message === "") return;

    const internalMessageSubmit = formMethods.handleSubmit(
      (form: InternalMessagesForm) =>
        createInternalMessage(
          form.internalMessages,
          response,
          relatedEventPrevUuid,
        ),
    );
    void internalMessageSubmit();
  };
